
import { Comment } from '../../types/comment';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Smile, Send, RefreshCw, Lock } from 'lucide-react';
import ReplyActions from './ReplyActions';
import { useEffect } from 'react';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';

interface ReplyComposerProps {
  replyContent: string;
  setReplyContent: (content: string) => void;
  isSubmitting: boolean;
  handleReply: () => void;
  comment: Comment;
}

const ReplyComposer = ({ 
  replyContent, 
  setReplyContent, 
  isSubmitting,
  handleReply,
  comment
}: ReplyComposerProps) => {
  const { isSubscriptionActive, redirectToPayment } = useSubscriptionStatus();
  
  // Use the AI-generated reply when the comment changes
  useEffect(() => {
    if (comment?.aiGeneratedReply && isSubscriptionActive) {
      setReplyContent(comment.aiGeneratedReply);
    }
  }, [comment?.id, comment?.aiGeneratedReply, setReplyContent, isSubscriptionActive]);

  if (!isSubscriptionActive) {
    return (
      <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Lock className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              La possibilité de répondre est réservée aux abonnés
            </span>
          </div>
          <Button 
            onClick={redirectToPayment}
            size="sm"
            className="bg-custom-primary hover:bg-custom-dark text-white"
          >
            Débloquer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Textarea
        placeholder={`Répondre à ${comment.author.name || 'ce commentaire'}...`}
        value={replyContent}
        onChange={(e) => setReplyContent(e.target.value)}
        className="min-h-[120px] bg-white dark:bg-gray-800/90 border-gray-300 dark:border-gray-700 transition-colors"
      />
      
      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" className="text-gray-600 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800">
            <Smile className="h-4 w-4 mr-1" />
            Emojis
          </Button>
          <ReplyActions />
        </div>
        
        <Button 
          onClick={handleReply} 
          disabled={!replyContent.trim() || isSubmitting}
          className="bg-custom-primary hover:bg-custom-dark text-white dark:bg-custom-secondaryLight dark:text-gray-900 dark:hover:bg-custom-secondaryLight/90"
        >
          {isSubmitting ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Envoi...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Envoyer
            </>
          )}
        </Button>
      </div>
    </>
  );
};

export default ReplyComposer;
